import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import DataTable, {
  DataTableColumnHeader, DataTablePagination
} from '../../ui/DataTable'
import Checkbox from '../../ui/Checkbox'
import Tooltip from '../../ui/Tooltip'
import EmptyState from '../../ui/EmptyState'
import SettingIcon from '../../ui/icons/SettingIcon'
import TriggeredAlertsRow from './TriggeredAlertsRow'
import {
  PRIMARY_COLUMN_KEYS,
  PRIMARY_COLUMN_LABELS,
  PRIMARY_COLUMN_ORDER,
  DEFAULT_COLUMN_PREFERENCES,
  EXTRA_COLUMN_TYPES,
  orderedColumnSlots
} from './constants'

const SETTINGS_BTN_CLASS = 'tw:bg-transparent tw:border-0 tw:cursor-pointer tw:text-gray-600 '
  + 'tw:inline-flex tw:items-center tw:align-middle '
  + 'tw:disabled:opacity-50 tw:disabled:cursor-not-allowed tw:flex-shrink-0'

const EMPTY_MESSAGE = 'Les alertes déclenchées apparaîtront ici une fois les données disponibles.'

// Map column ids (front-end) → backend sort keys. Columns without an entry are not sortable.
const SORT_KEYS = {
  [PRIMARY_COLUMN_KEYS.ID]: 'id',
  [PRIMARY_COLUMN_KEYS.ALERT_NAME]: 'alert_name',
  [PRIMARY_COLUMN_KEYS.STATUT]: 'issue_status',
  [PRIMARY_COLUMN_KEYS.CATEGORIE]: 'issue_category',
  [PRIMARY_COLUMN_KEYS.CREATED_AT]: 'created_at',
  [PRIMARY_COLUMN_KEYS.ATTRIBUTION]: 'attribution_name',
  [PRIMARY_COLUMN_KEYS.RESOURCE_NAME]: 'resource_name'
}
const SORT_KEY_BY_BACKEND = Object.fromEntries(
  Object.entries(SORT_KEYS).map(([columnId, backendKey]) => [backendKey, columnId])
)

// Variable extra columns are sortable through a dynamic "variable_<id>" key,
// Round-tripped to/from the "extra-variable-<id>" TanStack column id.
const VARIABLE_COLUMN_RE = /^extra-variable-(?<id>\d+)$/u
const VARIABLE_SORT_RE = /^variable_(?<id>\d+)$/u

const sortKeyForColumn = columnId => {
  const match = VARIABLE_COLUMN_RE.exec(columnId)
  return match ? `variable_${match.groups.id}` : SORT_KEYS[columnId]
}

const columnIdForSort = backendKey => {
  const match = VARIABLE_SORT_RE.exec(backendKey)
  return match ? `extra-variable-${match.groups.id}` : SORT_KEY_BY_BACKEND[backendKey]
}

const DEFAULT_ISSUES = []
const DEFAULT_VISIBLE_COLUMNS = { primary: DEFAULT_COLUMN_PREFERENCES.primaryColumns, extra: [] }
const EMPTY_STATE = <EmptyState message={EMPTY_MESSAGE} title="Aucune donnée" />

const buildPrimaryColumn = key => {
  const sortable = Boolean(SORT_KEYS[key])
  const renderSortable = ({ column }) =>
    <DataTableColumnHeader column={column} title={PRIMARY_COLUMN_LABELS[key]} />
  const renderPlain = () => <span>{PRIMARY_COLUMN_LABELS[key]}</span>
  return {
    id: key,
    accessorKey: key,
    enableSorting: sortable,
    header: sortable ? renderSortable : renderPlain
  }
}

// Extra columns can be either questions or ensemble variables — titles can be
// Arbitrarily long. Truncate visually + DS Tooltip on hover so the full label
// Remains readable. The id is prefixed with the type to keep TanStack column
// Ids unique even if a question and a variable share the same numeric id.
const EXTRA_HEADER_CLASS = 'tw:block tw:max-w-40 tw:truncate'

const buildExtraColumn = extra => {
  const sortable = extra.type === EXTRA_COLUMN_TYPES.VARIABLE
  const label = (
    <Tooltip className={EXTRA_HEADER_CLASS} content={extra.title}>
      {extra.title}
    </Tooltip>
  )
  let header = () => label
  if (sortable)
    header = ({ column }) => <DataTableColumnHeader column={column} title={label} />
  // Adding an accessorFn (cells come from TriggeredAlertsRow, not flexRender)
  // Turns this into an accessor column so TanStack honours enableSorting.
  return { id: `extra-${extra.type}-${extra.id}`, accessorFn: () => null, enableSorting: sortable, header }
}

const TriggeredAlertsTable = ({
  issues = DEFAULT_ISSUES,
  pagy,
  loading = false,
  error = null,
  sort = null,
  direction = 'desc',
  isIssueUpdating = null,
  activeIssueId = null,
  selectedIds, onToggleRow, onToggleAll,
  onSortChange, onPageChange, onPerPageChange,
  onIssueClick, onShareClick, onStatusEdit, onAttributionEdit, onColumnSettingsClick,
  onExportPdf,
  visibleColumns = DEFAULT_VISIBLE_COLUMNS
}) => {
  const primaryKeys = visibleColumns.primary
  const primaryColumns = useMemo(() => {
    // The ID column is mandatory even if a stored preference omits it.
    const keys = [...primaryKeys]
    if (!keys.includes(PRIMARY_COLUMN_KEYS.ID))
      keys.unshift(PRIMARY_COLUMN_KEYS.ID)
    return PRIMARY_COLUMN_ORDER.filter(key => keys.includes(key))
  }, [primaryKeys])
  const extraColumns = visibleColumns.extra
  const tableColumns = useMemo(() => orderedColumnSlots(primaryColumns, extraColumns).map(slot => {
    if (slot.kind === 'primary')
      return buildPrimaryColumn(slot.key)
    return buildExtraColumn(slot.column)
  }), [primaryColumns, extraColumns])

  const sortingState = useMemo(() => {
    if (!sort)
      return []
    const columnId = columnIdForSort(sort)
    return columnId ? [{ id: columnId, desc: direction === 'desc' }] : []
  }, [sort, direction])

  const handleSortingChange = useCallback(updater => {
    // Guard against spam-clicks while a fetch is in flight (cf. spam-click rule).
    if (loading)
      return
    const next = typeof updater === 'function' ? updater(sortingState) : updater
    if (next.length === 0) {
      onSortChange(null, 'desc')
      return
    }
    const [{ id, desc }] = next
    onSortChange(sortKeyForColumn(id), desc ? 'desc' : 'asc')
  }, [loading, sortingState, onSortChange])

  const visibleIds = useMemo(() => issues.map(issue => issue.id), [issues])
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id))
  const someSelected = !allSelected && visibleIds.some(id => selectedIds.has(id))
  const handleToggleAll = useCallback(() => onToggleAll(visibleIds), [onToggleAll, visibleIds])

  const renderSelectHeader = useCallback(() => (
    <Checkbox
      aria-label="Tout sélectionner"
      checked={allSelected}
      disabled={visibleIds.length === 0}
      indeterminate={someSelected}
      onChange={handleToggleAll}
    />
  ), [allSelected, someSelected, visibleIds.length, handleToggleAll])

  const cogButton = (
    <button
      aria-label="Personnaliser les colonnes"
      className={SETTINGS_BTN_CLASS}
      disabled={loading}
      type="button"
      onClick={onColumnSettingsClick}
    >
      <SettingIcon className="tw:w-5 tw:h-5" />
    </button>
  )

  const tableColumnsWithSelect = useMemo(() => [
    { id: '_select', enableSorting: false, header: renderSelectHeader },
    ...tableColumns
  ], [renderSelectHeader, tableColumns])

  const table = useReactTable({
    data: issues,
    columns: tableColumnsWithSelect,
    state: { sorting: sortingState },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    // Force uniform asc → desc → none for every column; numeric columns otherwise
    // Default desc-first, desyncing from the parent sort state.
    sortDescFirst: false,
    getRowId: row => String(row.id)
  })


  const renderRow = useCallback((row, atScrollEnd) => (
    <TriggeredAlertsRow
      key={row.id}
      atScrollEnd={atScrollEnd}
      disabled={isIssueUpdating ? isIssueUpdating(row.original.id) : false}
      extraColumns={extraColumns}
      hasOpenPopup={activeIssueId === row.original.id}
      issue={row.original}
      primaryColumns={primaryColumns}
      selected={selectedIds.has(row.original.id)}
      onAttributionEdit={onAttributionEdit}
      onExportPdf={onExportPdf}
      onIssueClick={onIssueClick}
      onShareClick={onShareClick}
      onStatusEdit={onStatusEdit}
      onToggleRow={onToggleRow}
    />
  ), [
    isIssueUpdating,
    extraColumns,
    activeIssueId,
    primaryColumns,
    selectedIds,
    onAttributionEdit,
    onExportPdf,
    onIssueClick,
    onShareClick,
    onStatusEdit,
    onToggleRow
  ])

  const pagination = (
    <DataTablePagination
      disabled={loading}
      pagy={pagy}
      onPageChange={onPageChange}
      onPerPageChange={onPerPageChange}
    />
  )

  return (
    <DataTable
      actionsHeader={cogButton}
      emptyState={EMPTY_STATE}
      error={error}
      loading={loading}
      pagination={pagination}
      renderRow={renderRow}
      table={table}
    />
  )
}

TriggeredAlertsTable.propTypes = {
  activeIssueId: PropTypes.number,
  direction: PropTypes.oneOf(['asc', 'desc']),
  error: PropTypes.instanceOf(Error),
  isIssueUpdating: PropTypes.func,
  issues: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired })),
  loading: PropTypes.bool,
  onAttributionEdit: PropTypes.func.isRequired,
  onColumnSettingsClick: PropTypes.func.isRequired,
  onExportPdf: PropTypes.func.isRequired,
  onIssueClick: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPerPageChange: PropTypes.func.isRequired,
  onShareClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onStatusEdit: PropTypes.func.isRequired,
  onToggleAll: PropTypes.func.isRequired,
  onToggleRow: PropTypes.func.isRequired,
  pagy: PropTypes.shape({
    count: PropTypes.number,
    items: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number
  }),
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  sort: PropTypes.string,
  visibleColumns: PropTypes.shape({
    extra: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired
    })),
    primary: PropTypes.arrayOf(PropTypes.string)
  })
}

TriggeredAlertsTable.defaultProps = {
  activeIssueId: null,
  direction: 'desc',
  error: null,
  isIssueUpdating: null,
  issues: DEFAULT_ISSUES,
  loading: false,
  pagy: null,
  sort: null,
  visibleColumns: DEFAULT_VISIBLE_COLUMNS
}

export default TriggeredAlertsTable
